import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import {
  Badge,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import type {
  TagInputChipProps,
  TagInputChipsProps,
  TagInputContentProps,
  TagInputGroup,
  TagInputProps,
} from './TagInput.types';
import { useClaimEscapeWhileOpen } from './useClaimEscapeWhileOpen';
import { useTranslation } from 'react-i18next';

type ChipsInputKeyDownHandler = NonNullable<ComponentProps<typeof ComboboxChipsInput>['onKeyDown']>;

/**
 * Phase 6 — `TagInput` compound (Shadcn-canonical).
 *
 * Generic dot-notation compound that composes v4 `<Combobox>` +
 * `<ComboboxChips>` + `<ComboboxCollection>` to replace the MUI
 * Autocomplete-based `TagInputField`:
 *
 * @example Object items
 * ```tsx
 * <TagInput<PersonaOption>
 *   value={selected}
 *   onChange={setSelected}
 *   options={options}
 *   getOptionLabel={(o) => o.label}
 *   getOptionValue={(o) => o.id}
 * >
 *   <TagInput.Chips renderChip={(item, onRemove) => <Badge>…<button onClick={onRemove}/></Badge>} />
 *   <TagInput.Content renderOption={(item) => <div>…</div>} />
 * </TagInput>
 * ```
 *
 * @example String items + freeSolo
 * ```tsx
 * <TagInput value={tags} onChange={setTags} options={suggestions} freeSolo>
 *   <TagInput.Chips placeholder="Type and hit enter" />
 *   <TagInput.Content />
 * </TagInput>
 * ```
 *
 * Composition contract (matches base-ui Combobox.Chips + DS):
 * - `items` is set on `<Combobox>` so base-ui's built-in filter can mark the
 *   popup `data-empty` when the typed query matches nothing. v4's
 *   `<ComboboxEmpty>` then becomes visible via its
 *   `group-data-empty/combobox-content:flex` selector.
 * - `<ComboboxCollection>` inside `<ComboboxList>` consumes the filtered
 *   items the Root computed and renders one `<ComboboxItem>` per match.
 * - Default chips compose `<ComboboxChip>` (which carries
 *   `data-slot="combobox-chip"` and an auto-rendered remove button driven
 *   by base-ui's chip-remove primitive). Custom `renderChip` keeps the
 *   `(item, onRemove)` shape and is wrapped in `<ComboboxChip>` so the
 *   `data-slot` anchor stays present (the chips container's
 *   `has-data-[slot=combobox-chip]:px-1.5` selector relies on it).
 *
 * Reference compound: `FeatureSidebar` (dot-notation slots).
 * Reference Shadcn pattern: https://ui.shadcn.com/docs/components/combobox
 */

interface TagInputContextValue {
  value: unknown[];
  onChange: (next: unknown[]) => void;
  options: unknown[];
  groups?: TagInputGroup<unknown>[];
  isOptionDisabled?: (item: unknown) => boolean;
  disabled: boolean;
  freeSolo: boolean;
  externallyFiltered: boolean;
  getOptionLabel: (item: unknown) => string;
  getOptionValue: (item: unknown) => string;
  isOptionEqualToValue: (a: unknown, b: unknown) => boolean;
  id?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  isListOpen: boolean;
  /**
   * Shared ref for the dropdown anchor. `<TagInput.Chips>` assigns this to the
   * `<ComboboxChips>` outer container so `<TagInput.Content>` can pass it as
   * the popover anchor. Without this, base-ui defaults the anchor to the
   * inner `<ComboboxChipsInput>` element — which shrinks horizontally as
   * chips are added, causing the popup width (`w-(--anchor-width)`) to
   * shrink with it.
   */
  anchorRef: RefObject<HTMLDivElement | null>;
}

const TagInputContext = createContext<TagInputContextValue | null>(null);

function useTagInputContext(component: string): TagInputContextValue {
  const ctx = useContext(TagInputContext);
  if (!ctx) {
    throw new Error(`<${component}> must be rendered inside <TagInput>`);
  }
  return ctx;
}

/**
 * Default getOptionLabel: stringifies any value. For objects, callers should
 * supply an explicit `getOptionLabel`.
 */
function defaultGetLabel<T>(item: T): string {
  return typeof item === 'string' ? item : String(item);
}

function TagInputRoot<T = string>(props: TagInputProps<T>) {
  const {
    value,
    onChange,
    options,
    groups,
    isOptionDisabled,
    disabled = false,
    freeSolo = false,
    getOptionLabel,
    getOptionValue,
    isOptionEqualToValue,
    id,
    inputValue,
    onInputValueChange,
    children,
  } = props;
  // `<FormControl>` hands these to its child, which is this root rather than an element, so
  // they would stop here unless passed on to the input with the id.
  const ariaInvalid = props['aria-invalid'];
  const ariaDescribedBy = props['aria-describedby'];

  // When the consumer drives the input value (controlled search query, e.g.
  // AddMembersSection routes the term to a remote API and pre-filters
  // `options`), we must disable base-ui's local filter — otherwise it would
  // double-filter on top of the already-filtered list and hide matches.
  const externallyFiltered = onInputValueChange !== undefined;

  // Stable ref shared with `<TagInput.Content>` to anchor the popover on the
  // full-width chips row instead of the inner input (which shrinks with chips).
  const anchorRef = useRef<HTMLDivElement>(null);

  const [isListOpen, setIsListOpen] = useState(false);
  useClaimEscapeWhileOpen(isListOpen);

  // Normalize accessors. For `T = string` defaults work without overrides.
  // The accessors and context value are computed in the same useMemo so that
  // their identities don't change every render (which would break referential
  // equality consumers downstream rely on).
  const contextValue = useMemo<TagInputContextValue>(() => {
    const labelFn = (getOptionLabel ?? defaultGetLabel) as (item: unknown) => string;
    const valueFn = (getOptionValue ?? labelFn) as (item: unknown) => string;
    const eqFn = (isOptionEqualToValue ?? ((a: T, b: T) => valueFn(a) === valueFn(b))) as (
      a: unknown,
      b: unknown,
    ) => boolean;

    return {
      value: value as unknown[],
      onChange: onChange as (next: unknown[]) => void,
      options: options as unknown[],
      groups: groups as TagInputGroup<unknown>[] | undefined,
      isOptionDisabled: isOptionDisabled as ((item: unknown) => boolean) | undefined,
      disabled,
      freeSolo,
      externallyFiltered,
      getOptionLabel: labelFn,
      getOptionValue: valueFn,
      isOptionEqualToValue: eqFn,
      id,
      ariaInvalid,
      ariaDescribedBy,
      isListOpen,
      anchorRef,
    };
  }, [
    value,
    onChange,
    options,
    groups,
    isOptionDisabled,
    disabled,
    freeSolo,
    externallyFiltered,
    getOptionLabel,
    getOptionValue,
    isOptionEqualToValue,
    id,
    ariaInvalid,
    ariaDescribedBy,
    isListOpen,
  ]);

  return (
    <TagInputContext.Provider value={contextValue}>
      <Combobox<T, true>
        multiple
        // v4 Combobox renames base-ui's `selectedValue` / `onSelectedValueChange`
        // to `value` / `onValueChange` (the API surface omits base-ui's
        // input-value pair). So this IS the committed selection.
        value={value}
        onValueChange={onChange}
        // Base UI takes sections as `{ items }` objects in the same prop.
        items={groups ?? options}
        disabled={disabled}
        // Disable base-ui's filter when the consumer pre-filters externally.
        // Otherwise base-ui matches `inputValue` against `itemToStringLabel`
        // for free, which is what powers the data-empty signal for v4's
        // ComboboxEmpty.
        filter={externallyFiltered ? null : undefined}
        inputValue={inputValue}
        onInputValueChange={onInputValueChange}
        itemToStringLabel={getOptionLabel}
        itemToStringValue={getOptionValue}
        isItemEqualToValue={contextValue.isOptionEqualToValue}
        onOpenChange={setIsListOpen}
      >
        {children}
      </Combobox>
    </TagInputContext.Provider>
  );
}

function TagInputChips<T = string>({
  placeholder,
  ariaLabel,
  required,
  renderChip,
  className,
}: TagInputChipsProps<T>) {
  const { t } = useTranslation('common');

  const ctx = useTagInputContext('TagInput.Chips');

  // Free-solo: capture Enter to push the typed text into the value array.
  const commitTypedTag = (event: KeyboardEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    const text = target.value.trim();
    if (text === '') return;

    // Avoid duplicates by string equality on the rendered label.
    const exists = ctx.value.some((v) => ctx.getOptionLabel(v) === text);
    if (exists) {
      target.value = '';
      return;
    }

    event.preventDefault();
    ctx.onChange([...ctx.value, text as unknown]);
    target.value = '';
  };

  const handleKeyDown: ChipsInputKeyDownHandler = (event) => {
    if (event.key === 'Escape' && !ctx.isListOpen) {
      // With the list closed, Base UI answers Escape by clearing the whole selection, so the
      // key people press to leave a field would throw away every chip in it.
      event.preventBaseUIHandler();
      return;
    }
    if (ctx.freeSolo && event.key === 'Enter') commitTypedTag(event);
  };

  return (
    /*
     * Tessra's ComboboxChips paints its border with `--input`, which is a fill token, so
     * the field reads as borderless beside the Inputs and Selects it sits with, and it
     * stops a step short on radius. Until the design system corrects the token, borrow
     * both from those siblings. The container stays unfilled on purpose: the chips carry
     * `--accent`, which is too close to `--input` to survive on top of it.
     */
    // One provider for the row rather than one per chip: a field with twenty approvers
    // otherwise mounts twenty.
    <TooltipProvider>
      <ComboboxChips ref={ctx.anchorRef} className={twMerge('rounded-lg border-border', className)}>
        {ctx.value.map((item, index) => {
          const label = ctx.getOptionLabel(item);
          const onRemove = () => {
            const next = ctx.value.filter((_, i) => i !== index);
            ctx.onChange(next);
          };

          if (renderChip) {
            // Custom chip — wrap in ComboboxChip (showRemove=false: caller
            // supplies its own remove affordance via the onRemove callback).
            // The wrapper preserves the data-slot="combobox-chip" anchor that
            // ComboboxChips' `has-data-[slot=combobox-chip]:px-1.5` relies on
            // and inherits keyboard navigation (←/→/Backspace) from base-ui.
            return (
              <ComboboxChip
                key={`${ctx.getOptionValue(item)}:${index}`}
                showRemove={false}
                className="bg-transparent p-0 font-normal h-auto"
              >
                {renderChip(item as T, onRemove)}
              </ComboboxChip>
            );
          }

          // Default chip — let v4 ComboboxChip render with showRemove=true.
          // base-ui's <ChipRemove> internally calls store.setSelectedValue(...)
          // which surfaces via Combobox.Root's onValueChange → our onChange,
          // so removal stays in sync without a manual handler.
          //
          // MFINS-2966 — override v4's default gray chip (`bg-muted`,
          // `text-foreground`, `rounded-sm`) with the design-system blue accent
          // pill: light-blue background, blue text, fully rounded. The classes
          // merge over the variant defaults via tailwind-variants. The
          // auto-rendered X (an XIcon using currentColor) inherits the blue text.
          return (
            <ComboboxChip
              key={`${ctx.getOptionValue(item)}:${index}`}
              aria-label={t('ui.tagInput.removeTag', { label })}
              className="rounded-full bg-accent px-2.5 text-accent-foreground"
            >
              {label}
            </ComboboxChip>
          );
        })}
        <ComboboxChipsInput
          id={ctx.id}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-required={required === true ? true : undefined}
          aria-invalid={ctx.ariaInvalid}
          aria-describedby={ctx.ariaDescribedBy}
          disabled={ctx.disabled}
          onKeyDown={handleKeyDown}
        />
        {/* A trigger, not an icon: the chevron used to be decoration over nothing, so clicking it
          — or any part of the field other than the input — did not open the list. */}
        <ComboboxTrigger
          data-testid="tag-input-chevron"
          aria-label={t('ui.tagInput.openList')}
          className="ml-auto"
        />
      </ComboboxChips>
    </TooltipProvider>
  );
}

function TagInputContent<T = string>({ renderOption, side }: TagInputContentProps<T>) {
  const { t } = useTranslation('common');

  const ctx = useTagInputContext('TagInput.Content');

  const renderItem = (item: unknown) => (
    <ComboboxItem
      key={ctx.getOptionValue(item)}
      value={item}
      disabled={ctx.isOptionDisabled?.(item)}
    >
      {renderOption ? renderOption(item as T) : ctx.getOptionLabel(item)}
    </ComboboxItem>
  );

  return (
    <ComboboxContent
      side={side}
      anchor={ctx.anchorRef}
      // When this TagInput renders inside a Radix Dialog with `modal=true`,
      // Radix sets `body { pointer-events: none }` and only the
      // DialogOverlay + DialogContent get `pointer-events: auto` so that
      // outside elements can't be clicked. ComboboxContent portals to
      // document.body — without an explicit `pointer-events-auto` it
      // inherits `none` and the dropdown items become unclickable
      // (the list is visible but selection commits never fire).
      // This override is harmless outside a modal Dialog and required
      // Inside one. See Phase 6 follow-up.
      className="pointer-events-auto"
    >
      {ctx.groups ? (
        <>
          {/* A list of sections renders through a function, so the empty state sits beside it. */}
          <ComboboxEmpty>{t('ui.tagInput.noResults')}</ComboboxEmpty>
          <ComboboxList>
            {(group: TagInputGroup<unknown>) => (
              <ComboboxGroup key={group.label} items={group.items}>
                <ComboboxLabel>{group.label}</ComboboxLabel>
                <ComboboxCollection>{renderItem}</ComboboxCollection>
              </ComboboxGroup>
            )}
          </ComboboxList>
        </>
      ) : (
        <ComboboxList>
          <ComboboxCollection>{renderItem}</ComboboxCollection>
          <ComboboxEmpty>{t('ui.tagInput.noResults')}</ComboboxEmpty>
        </ComboboxList>
      )}
    </ComboboxContent>
  );
}

/**
 * The avatar chip every person- and persona-picker in the app draws.
 *
 * Tessra's `ComboboxChip` has no leading slot, so a chip with a face in front of
 * it cannot be built from the design system alone. Four features had each pasted
 * the same Badge-plus-Avatar-plus-remove-button markup; this is that markup, once.
 */
export function TagInputChip(props: TagInputChipProps) {
  const { avatar, label, onRemove } = props;

  return (
    <Badge
      title={props.title}
      variant={props.variant ?? 'secondary'}
      roundness="full"
      className="gap-1.5 py-1"
    >
      {avatar}
      {label}
      {onRemove && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              /*
               * ComboboxChips is a base-ui composite (`role="toolbar"`), so its items
               * carry `tabindex="-1"` and arrow keys move between them. A button left
               * at the browser default becomes a tab stop inside that composite and
               * swallows Tab; the chip beside it is reached with the arrow keys.
               */
              tabIndex={-1}
              aria-label={props.removeLabel}
              onClick={onRemove}
              className="ml-0.5 rounded-full p-0.5 hover:bg-surface-tertiary"
            >
              <XIcon aria-hidden className="size-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{props.removeLabel}</TooltipContent>
        </Tooltip>
      )}
    </Badge>
  );
}

TagInputChips.displayName = 'TagInput.Chips';
TagInputContent.displayName = 'TagInput.Content';
TagInputChip.displayName = 'TagInput.Chip';

// Object.assign loses the generic on subcomponents; consumers can pass
// `<TagInput<T>>` to drive type inference at the root.
export const TagInput = Object.assign(TagInputRoot, {
  Chips: TagInputChips,
  Chip: TagInputChip,
  Content: TagInputContent,
}) as (<T = string>(props: TagInputProps<T>) => React.JSX.Element) & {
  Chips: typeof TagInputChips;
  Chip: typeof TagInputChip;
  Content: typeof TagInputContent;
};
